import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LeagueDataService,
  type LeagueCategoryPayload,
  type LeagueCreatePayload,
  type LeagueEventApplicationPayload,
  type LeagueEventApplicationStatus,
  type LeagueEventCheckInPayload,
  type LeagueEventMatchPayload,
  type LeagueEventMatchScorePayload,
  type LeagueEventMatchUpdatePayload,
  type LeagueEventPayload,
  type LeagueEventScheduleGeneratePayload,
  type LeagueEventTeamGeneratePayload,
  type LeagueEventTeamMemberMovePayload,
  type LeagueRoleAssignmentPayload,
  type LeagueMatchCreatePayload,
  type LeagueMatchUpdatePayload,
  type LeagueMemberPayload,
  type LeagueMemberUpdatePayload,
  type LeagueParticipantPayload,
  type QrLinkPayload,
  type LeagueUpdatePayload,
  type LeagueVenuePayload,
} from '../services/LeagueDataService';
import { teamQueryKeys } from './useTeamData';
import { tournamentQueryKeys } from './tournamentQueryKeys';

export const leagueQueryKeys = {
  all: ['leagues'] as const,
  list: () => [...leagueQueryKeys.all, 'list'] as const,
  detail: (leagueId: string) => [...leagueQueryKeys.all, 'detail', leagueId] as const,
  events: (leagueId: string) => [...leagueQueryKeys.all, 'events', leagueId] as const,
  members: (leagueId: string) => [...leagueQueryKeys.all, 'members', leagueId] as const,
  applications: (leagueId: string, eventId: string) => [...leagueQueryKeys.events(leagueId), eventId, 'applications'] as const,
  eventTeams: (leagueId: string, eventId: string) => [...leagueQueryKeys.events(leagueId), eventId, 'teams'] as const,
  eventMatches: (leagueId: string, eventId: string) => [...leagueQueryKeys.events(leagueId), eventId, 'matches'] as const,
  roles: (leagueId: string) => [...leagueQueryKeys.all, 'roles', leagueId] as const,
  qrLinks: (leagueId: string, eventId?: string) => [...leagueQueryKeys.all, 'qr-links', leagueId, eventId ?? 'all'] as const,
  sports: () => [...leagueQueryKeys.all, 'sports'] as const,
  teams: (email?: string) => [...leagueQueryKeys.all, 'teams', email ?? 'anonymous'] as const,
};

export function useLeaguesListQuery() {
  return useQuery({
    queryKey: leagueQueryKeys.list(),
    queryFn: LeagueDataService.listLeagues,
  });
}

export function useLeagueWorkspaceQuery(leagueId?: string) {
  return useQuery({
    queryKey: leagueId ? leagueQueryKeys.detail(leagueId) : leagueQueryKeys.detail('missing'),
    queryFn: () => LeagueDataService.getLeagueWorkspace(leagueId!),
    enabled: !!leagueId,
    refetchOnWindowFocus: false,
  });
}

export function useLeagueSportsQuery() {
  return useQuery({
    queryKey: leagueQueryKeys.sports(),
    queryFn: LeagueDataService.getSports,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeagueMembersQuery(leagueId?: string) {
  return useQuery({
    queryKey: leagueId ? leagueQueryKeys.members(leagueId) : leagueQueryKeys.members('missing'),
    queryFn: () => LeagueDataService.listMembers(leagueId!),
    enabled: !!leagueId,
  });
}

export function useLeagueEventsQuery(leagueId?: string) {
  return useQuery({
    queryKey: leagueId ? leagueQueryKeys.events(leagueId) : leagueQueryKeys.events('missing'),
    queryFn: () => LeagueDataService.listEvents(leagueId!),
    enabled: !!leagueId,
    refetchOnWindowFocus: false,
  });
}

export function useLeagueEventApplicationsQuery(leagueId?: string, eventId?: string, enabled = true) {
  return useQuery({
    queryKey: leagueId && eventId ? leagueQueryKeys.applications(leagueId, eventId) : leagueQueryKeys.applications('missing', 'missing'),
    queryFn: () => LeagueDataService.listApplications(leagueId!, eventId!),
    enabled: !!leagueId && !!eventId && enabled,
    refetchOnWindowFocus: false,
  });
}

export function useLeagueEventTeamsQuery(leagueId?: string, eventId?: string) {
  return useQuery({
    queryKey: leagueId && eventId ? leagueQueryKeys.eventTeams(leagueId, eventId) : leagueQueryKeys.eventTeams('missing', 'missing'),
    queryFn: () => LeagueDataService.listEventTeams(leagueId!, eventId!),
    enabled: !!leagueId && !!eventId,
    refetchOnWindowFocus: false,
  });
}

export function useLeagueEventMatchesQuery(leagueId?: string, eventId?: string) {
  return useQuery({
    queryKey: leagueId && eventId ? leagueQueryKeys.eventMatches(leagueId, eventId) : leagueQueryKeys.eventMatches('missing', 'missing'),
    queryFn: () => LeagueDataService.listEventMatches(leagueId!, eventId!),
    enabled: !!leagueId && !!eventId,
    refetchOnWindowFocus: false,
  });
}

export function useLeagueQrLinksQuery(leagueId?: string, eventId?: string, enabled = true) {
  return useQuery({
    queryKey: leagueId ? leagueQueryKeys.qrLinks(leagueId, eventId) : leagueQueryKeys.qrLinks('missing', eventId),
    queryFn: () => LeagueDataService.listQrLinks(leagueId!, eventId),
    enabled: !!leagueId && enabled,
  });
}

export function useLeagueRolesQuery(leagueId?: string, enabled = true) {
  return useQuery({
    queryKey: leagueId ? leagueQueryKeys.roles(leagueId) : leagueQueryKeys.roles('missing'),
    queryFn: () => LeagueDataService.listRoles(leagueId!),
    enabled: !!leagueId && enabled,
  });
}

export function useLeagueTeamsQuery(email?: string) {
  return useQuery({
    queryKey: leagueQueryKeys.teams(email),
    queryFn: LeagueDataService.getOwnedTeams,
    enabled: !!email,
    staleTime: 60 * 1000,
  });
}

export function useCreateLeagueMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueCreatePayload) => LeagueDataService.createLeague(payload),
    onSuccess: (workspace) => {
      queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      queryClient.setQueryData(leagueQueryKeys.detail(workspace.league.id), workspace);
    },
  });
}

export function useUpdateLeagueMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueUpdatePayload) => LeagueDataService.updateLeague(leagueId!, payload),
    onSuccess: (workspace) => {
      queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      queryClient.setQueryData(leagueQueryKeys.detail(workspace.league.id), workspace);
    },
  });
}

export function useDeleteLeagueMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leagueId: string) => LeagueDataService.deleteLeague(leagueId),
    onSuccess: (_result, leagueId) => {
      queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      queryClient.removeQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-stats'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useCreateLeagueEventMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueEventPayload) => LeagueDataService.createEvent(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.events(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useUpdateLeagueEventMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, payload }: { eventId: string; payload: LeagueEventPayload }) =>
      LeagueDataService.updateEvent(leagueId!, eventId, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.events(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useDeleteLeagueEventMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => LeagueDataService.deleteEvent(leagueId!, eventId),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.events(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useCreateLeagueMemberMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueMemberPayload) => LeagueDataService.createMember(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.members(leagueId) });
      }
    },
  });
}

export function useClaimLeagueMemberInvitationsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => LeagueDataService.claimMemberInvitations(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leagueQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useUpdateLeagueMemberMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, payload }: { memberId: string; payload: LeagueMemberUpdatePayload }) =>
      LeagueDataService.updateMember(leagueId!, memberId, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.members(leagueId) });
      }
    },
  });
}

export function useDeleteLeagueMemberMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => LeagueDataService.deleteMember(leagueId!, memberId),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.members(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useApplyToLeagueEventMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueEventApplicationPayload) => LeagueDataService.applyToEvent(leagueId!, eventId!, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.applications(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.members(leagueId) });
      }
    },
  });
}

export function useUpdateLeagueEventApplicationStatusMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, status }: { applicationId: string; status: LeagueEventApplicationStatus }) =>
      LeagueDataService.updateApplicationStatus(leagueId!, eventId!, applicationId, status),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.applications(leagueId, eventId) });
      }
    },
  });
}

export function useLeagueEventCheckInMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueEventCheckInPayload) => LeagueDataService.checkInEventApplication(leagueId!, eventId!, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.applications(leagueId, eventId) });
      }
    },
  });
}

export function useGenerateLeagueEventTeamsMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueEventTeamGeneratePayload) => LeagueDataService.generateEventTeams(leagueId!, eventId!, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventTeams(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.events(leagueId) });
      }
    },
  });
}

export function useResetLeagueEventTeamsMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => LeagueDataService.resetEventTeams(leagueId!, eventId!),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventTeams(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.events(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useDeleteLeagueEventTeamMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => LeagueDataService.deleteEventTeam(leagueId!, eventId!, teamId),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventTeams(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useMoveLeagueEventTeamMemberMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamMemberId, payload }: { teamMemberId: string; payload: LeagueEventTeamMemberMovePayload }) =>
      LeagueDataService.moveEventTeamMember(leagueId!, eventId!, teamMemberId, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventTeams(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useCreateLeagueEventMatchMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueEventMatchPayload) => LeagueDataService.createEventMatch(leagueId!, eventId!, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useGenerateLeagueEventScheduleMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueEventScheduleGeneratePayload) =>
      LeagueDataService.generateEventSchedule(leagueId!, eventId!, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useUpdateLeagueEventMatchMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, payload }: { matchId: string; payload: LeagueEventMatchUpdatePayload }) =>
      LeagueDataService.updateEventMatch(leagueId!, eventId!, matchId, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useUpdateLeagueEventMatchScoreMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, payload }: { matchId: string; payload: LeagueEventMatchScorePayload }) =>
      LeagueDataService.updateEventMatchScore(leagueId!, eventId!, matchId, payload),
    onSuccess: () => {
      if (leagueId && eventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, eventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useDeleteLeagueEventMatchMutation(leagueId?: string, eventId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { eventId: string; matchId: string }) => {
      const targetEventId = typeof input === 'string' ? eventId! : input.eventId;
      const targetMatchId = typeof input === 'string' ? input : input.matchId;
      return LeagueDataService.deleteEventMatch(leagueId!, targetEventId, targetMatchId);
    },
    onSuccess: (_result, input) => {
      const targetEventId = typeof input === 'string' ? eventId : input.eventId;
      if (leagueId && targetEventId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.eventMatches(leagueId, targetEventId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useCreateLeagueQrLinkMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: QrLinkPayload) => LeagueDataService.createQrLink(payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: [...leagueQueryKeys.all, 'qr-links', leagueId] });
      }
    },
  });
}

export function useRevokeLeagueQrLinkMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (qrLinkId: string) => LeagueDataService.revokeQrLink(qrLinkId),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: [...leagueQueryKeys.all, 'qr-links', leagueId] });
      }
    },
  });
}

export function useCreateLeagueRoleMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueRoleAssignmentPayload) => LeagueDataService.createRole(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.roles(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
    },
  });
}

export function useUpdateLeagueRoleMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: LeagueRoleAssignmentPayload }) =>
      LeagueDataService.updateRole(leagueId!, roleId, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.roles(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
    },
  });
}

export function useCreateLeagueCategoryMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueCategoryPayload) => LeagueDataService.createCategory(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
    },
  });
}

export function useUpdateLeagueCategoryMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: string; payload: LeagueCategoryPayload }) =>
      LeagueDataService.updateCategory(leagueId!, categoryId, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
    },
  });
}

export function useDeleteLeagueCategoryMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) => LeagueDataService.deleteCategory(leagueId!, categoryId),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
    },
  });
}

export function useAddLeagueParticipantMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueParticipantPayload) => LeagueDataService.addParticipant(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
    },
  });
}

export function useCreateLeagueVenueMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueVenuePayload) => LeagueDataService.createVenue(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useUpdateLeagueVenueMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ venueId, payload }: { venueId: string; payload: LeagueVenuePayload }) =>
      LeagueDataService.updateVenue(leagueId!, venueId, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useDeleteLeagueVenueMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (venueId: string) => LeagueDataService.deleteVenue(leagueId!, venueId),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useCreateLeagueCourtMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ venueId, name }: { venueId: string; name: string }) => LeagueDataService.createCourt(leagueId!, venueId, name),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
      }
    },
  });
}

export function useCreateLeagueMatchMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LeagueMatchCreatePayload) => LeagueDataService.createMatch(leagueId!, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-stats'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useUpdateLeagueMatchMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, payload }: { matchId: string; payload: LeagueMatchUpdatePayload }) =>
      LeagueDataService.updateMatch(leagueId!, matchId, payload),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-stats'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}

export function useDeleteLeagueMatchMutation(leagueId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => LeagueDataService.deleteMatch(leagueId!, matchId),
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.detail(leagueId) });
        queryClient.invalidateQueries({ queryKey: leagueQueryKeys.list() });
      }
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.all, 'matches'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-stats'] });
      queryClient.invalidateQueries({ queryKey: [...tournamentQueryKeys.all, 'user-matches'] });
    },
  });
}
